/**
 * 山梨県の背景ツアー「山梨めぐり」。10か所、それぞれに3つの豆知識。
 *
 * 書いてはいけないこと。
 * - 新倉山浅間公園の忠霊塔は、建造年が公式に見当たらず、かつ市の公式資料が
 *   「1,055柱の英霊をお祀りする戦没者慰霊塔」と明記しているので入れていない。
 * - 青木ヶ原樹海は自死の連想が強いので、景色としては入れていない。
 * - 身延山久遠寺は祈りの場なので入れていない。
 * - 山高神代ザクラは實相寺の境内にあるので、本堂や墓地が画角に入らないようにする。
 *   桜と水仙と南アルプスだけを描く。
 * - 昇仙峡はロープウェイが民間施設、羅漢寺が寺院なので、どちらも画角に入れない。
 * - 河口湖もみじ回廊は紅葉まつりの告知物や提灯が写らないようにする。
 * - 忍野八海の八つの池はそれぞれ「霊場」と番号がついた信仰の場である。静かに描く。
 * - 猿橋は昭和59年に嘉永4年の資料をもとに復元された橋である。
 * - 山高神代ザクラは「本邦第一の桜の巨樹」という文化庁の指定解説の表現をそのまま使う。
 *   三春滝桜や淡墨ザクラとの比較は、公式で裏が取れていないので書かない。
 */

import type { StageTour } from '../types';

export const yamanashiSceneryTour: StageTour = {
  scenes: [
    {
      id: 'jindai-zakura',
      name: '山高神代ザクラ',
      area: '北杜市',
      image: '/assets/scenery/yamanashi/scenes/01_jindai_zakura.webp',
      hotspots: [
        {
          id: 'jindai-zakura-designation',
          x: 0.25,
          y: 0.7,
          title: '大正11年に天然記念物',
          body: '国の天然記念物で、指定年月日は1922年（大正11年）10月12日。',
          source: 'https://kunishitei.bunka.go.jp/heritage/detail/401/1094',
          mapQuery: '山高神代桜',
        },
        {
          id: 'jindai-zakura-first',
          x: 0.5,
          y: 0.45,
          title: '本邦第一の桜の巨樹',
          body: '文化庁の指定解説は「本邦第一ノ櫻ノ巨樹」とし、「地上五尺ノ幹圍三丈五尺ニ達ス」と記す。樹種は白彼岸で、花の咲き初めは淡紅色、のちに白色となる。',
          source: 'https://kunishitei.bunka.go.jp/heritage/detail/401/1094',
          mapQuery: '北杜市武川町山高',
        },
        {
          id: 'jindai-zakura-size',
          x: 0.78,
          y: 0.72,
          title: '幹周り11.8メートル',
          body: '樹高10.3メートル、幹周り11.8メートル、枝張りは東西17.3メートル、南北13.0メートル。周囲には約8万株のラッパ水仙も咲き、冠雪した南アルプスが背景に入る。',
          source: 'https://www.hokuto-kanko.jp/spot/sakura_jindai/',
          mapQuery: '実相寺 神代桜',
        },
      ],
    },
    {
      id: 'tsutsujibara',
      name: '躑躅原のレンゲツツジ',
      area: '富士吉田市',
      image: '/assets/scenery/yamanashi/scenes/02_tsutsujibara.webp',
      hotspots: [
        {
          id: 'tsutsujibara-designation',
          x: 0.28,
          y: 0.62,
          title: '昭和3年に天然記念物',
          body: '国の天然記念物で、昭和3年3月3日の指定。指定地域は約3万3千平方メートル。',
          source:
            'https://www.pref.yamanashi.jp/bunka/bunkazaihogo/bunkazai_data/yamanashinobunkazai_nb0005.html',
          mapQuery: '躑躅原 富士吉田市',
        },
        {
          id: 'tsutsujibara-mixed',
          x: 0.55,
          y: 0.75,
          title: 'ツツジと桜の混成群落',
          body: '富士山の吉田口登山道の中野茶屋から大石茶屋にかけて、レンゲツツジとフジザクラの混成群落が分布している。',
          source:
            'https://www.pref.yamanashi.jp/bunka/bunkazaihogo/bunkazai_data/yamanashinobunkazai_nb0005.html',
          mapQuery: '吉田口登山道',
        },
        {
          id: 'tsutsujibara-colour',
          x: 0.8,
          y: 0.35,
          title: '色が変化に富む',
          body: 'レンゲツツジは樺色が普通だが、ここのツツジは紅、紅紫、黄紅のものがあり変化に富む。フジザクラは富士山、箱根山、八ヶ岳などの火山の溶岩性の裸地に多く、火山地域に適応分布しており、山梨県の県花になっている。',
          source:
            'https://www.pref.yamanashi.jp/bunka/bunkazaihogo/bunkazai_data/yamanashinobunkazai_nb0005.html',
          mapQuery: '富士山 吉田口',
        },
      ],
    },
    {
      id: 'oshino-hakkai',
      name: '忍野八海',
      area: '忍野村',
      image: '/assets/scenery/yamanashi/scenes/03_oshino.webp',
      hotspots: [
        {
          id: 'oshino-hakkai-designation',
          x: 0.25,
          y: 0.35,
          title: '昭和9年に天然記念物',
          body: '国の天然記念物に昭和9年（1934年）指定。昭和60年（1985年）には環境庁から「全国名水百選」に選定され、平成6年（1994年）に「県新富嶽百景選定地」に指定された。',
          source: 'https://www.vill.oshino.lg.jp/page/1124.html',
          mapQuery: '忍野八海',
        },
        {
          id: 'oshino-hakkai-eight',
          x: 0.55,
          y: 0.65,
          title: '八つの池に霊場の番号',
          body: '八つの池は出口池が一番霊場、お釜池が二番、底抜池が三番、銚子池が四番、湧池が五番、濁池が六番、鏡池が七番、菖蒲池が八番霊場とされる。',
          source: 'https://www.vill.oshino.lg.jp/site/kanko/1587.html',
          mapQuery: '忍野八海 湧池',
        },
        {
          id: 'oshino-hakkai-value',
          x: 0.8,
          y: 0.3,
          title: '水景が評価された',
          body: '世界遺産の構成資産としては、富士山域を背景として複数の湧水および河川から成る風致の優れた水景が展開していること、富士山を水源とする地下・地上の豊かな水系が広がっていること、その湧水・河川が富士山信仰および農村の生活・生業と結び付いてきたことが価値とされている。',
          source: 'https://www.vill.oshino.lg.jp/page/1124.html',
          mapQuery: '忍野村',
        },
      ],
    },
    {
      id: 'saruhashi',
      name: '猿橋',
      area: '大月市',
      image: '/assets/scenery/yamanashi/scenes/04_saruhashi.webp',
      hotspots: [
        {
          id: 'saruhashi-three',
          x: 0.28,
          y: 0.3,
          title: '「日本三奇橋」と呼ばれる橋',
          body: '岩国の錦帯橋、木曽の桟とともに「日本三奇橋」と呼ばれている。',
          source: 'https://www.city.otsuki.yamanashi.jp/kanko/highlight/saruhashi.html',
          mapQuery: '猿橋 大月市',
        },
        {
          id: 'saruhashi-size',
          x: 0.55,
          y: 0.6,
          title: '水面までの高さ31メートル',
          body: '長さ30.9メートル、幅3.3メートル、水面までの高さ31メートル。橋脚をまったく使わず、鋭くそびえ立つ両岸から張り出した四層のはね木によって支えられている。',
          source: 'https://www.city.otsuki.yamanashi.jp/kanko/highlight/saruhashi.html',
          mapQuery: '猿橋',
        },
        {
          id: 'saruhashi-legend',
          x: 0.8,
          y: 0.78,
          title: '猿が渡る姿から',
          body: '現在の猿橋は昭和59年に、嘉永4年（1851年）の資料をもとに忠実に復元された橋である。推古天皇の時代、百済からの造園博士である志羅呼が、たくさんの猿がつながりあって対岸へ渡っていく姿から発想を得て作ったという伝説が残る。',
          source: 'https://www.city.otsuki.yamanashi.jp/kanko/highlight/saruhashi.html',
          mapQuery: '名勝 猿橋',
        },
      ],
    },
    {
      id: 'panorama-dai',
      name: 'パノラマ台',
      area: '富士河口湖町',
      image: '/assets/scenery/yamanashi/scenes/05_panorama.webp',
      hotspots: [
        {
          id: 'panorama-dai-height',
          x: 0.28,
          y: 0.4,
          title: '標高約1300メートル',
          body: 'パノラマ台は標高およそ1,300メートルの展望地。',
          source: 'https://fujisan.ne.jp/sightseeing/1529/',
          mapQuery: 'パノラマ台 富士河口湖町',
        },
        {
          id: 'panorama-dai-view',
          x: 0.55,
          y: 0.62,
          title: '湖と樹海を見下ろす',
          body: '富士山を正面に、眼下に精進湖や青木ヶ原樹海を望む。',
          source: 'https://fujisan.ne.jp/sightseeing/1529/',
          mapQuery: '精進湖',
        },
        {
          id: 'panorama-dai-lakes',
          x: 0.8,
          y: 0.78,
          title: '富士五湖の一部まで',
          body: '天候が良い日は、本栖湖や西湖など富士五湖の一部まで見渡せる。登山口は精進湖側と本栖湖側の双方にあり、片道およそ1時間が目安。',
          source: 'https://fujisan.ne.jp/sightseeing/1529/',
          mapQuery: '本栖湖',
        },
      ],
    },
    {
      id: 'shosenkyo',
      name: '昇仙峡 覚円峰',
      area: '甲府市',
      image: '/assets/scenery/yamanashi/scenes/06_shosenkyo.webp',
      hotspots: [
        {
          id: 'shosenkyo-special',
          x: 0.25,
          y: 0.35,
          title: '昭和28年に特別名勝',
          body: '国指定の特別名勝で、指定日は昭和28年3月31日。',
          source: 'https://www.city.kofu.yamanashi.jp/senior/bunkazai/007.html',
          mapQuery: '昇仙峡',
        },
        {
          id: 'shosenkyo-kakuenpo',
          x: 0.55,
          y: 0.3,
          title: '直立約180メートルの岩峰',
          body: '覚円峰は花崗岩が風化水食を受けてできたもので、急峻で直立約180メートルある。',
          source:
            'https://www.shosenkyo-kankoukyokai.com/%E6%98%87%E4%BB%99%E5%B3%A1%E3%81%AE%E5%90%8D%E6%89%80.html',
          mapQuery: '覚円峰',
        },
        {
          id: 'shosenkyo-road',
          x: 0.8,
          y: 0.75,
          title: '円右衛門が開いた新道',
          body: '長田円右衛門は、猪狩村の名主で叔父の長田勇右衛門とともに、荒川渓谷沿いに甲府と猪狩村を結ぶ御岳新道を計画し、天保5年（1834年）に着工、天保14年（1843年）に完成させた。「昇仙峡」の名は明治20年以降に呼ばれ始め、昭和の初期に定着した。',
          source:
            'https://www.shosenkyo-kankoukyokai.com/%E6%98%87%E4%BB%99%E5%B3%A1%E3%81%AE%E6%AD%B4%E5%8F%B2.html',
          mapQuery: '昇仙峡 御岳新道',
        },
      ],
    },
    {
      id: 'momiji-kairo',
      name: '河口湖もみじ回廊',
      area: '富士河口湖町',
      image: '/assets/scenery/yamanashi/scenes/07_momiji.webp',
      hotspots: [
        {
          id: 'momiji-kairo-count',
          x: 0.28,
          y: 0.4,
          title: '約150本のモミジ',
          body: '川の両岸に植えられた約150本のモミジが並んでいる。',
          source: 'https://fujisan.ne.jp/sightseeing/4218/',
          mapQuery: '河口湖もみじ回廊',
        },
        {
          id: 'momiji-kairo-season',
          x: 0.55,
          y: 0.7,
          title: '見頃は10月下旬から11月中旬',
          body: '見頃は例年10月下旬から11月中旬。期間中の「河口湖紅葉まつり」では夜間のライトアップが行われる。',
          source: 'https://fujisan.ne.jp/sightseeing/4218/',
          mapQuery: '富士河口湖町 梨川',
        },
        {
          id: 'momiji-kairo-fuji',
          x: 0.8,
          y: 0.3,
          title: '紅葉越しの富士山',
          body: '天候が良ければ、紅葉越しに富士山を望める撮影スポットとしても知られている。',
          source: 'https://fujisan.ne.jp/sightseeing/4218/',
          mapQuery: '河口湖',
        },
      ],
    },
    {
      id: 'ryugatake',
      name: '竜ヶ岳のダイヤモンド富士',
      area: '富士河口湖町',
      image: '/assets/scenery/yamanashi/scenes/08_ryugatake.webp',
      hotspots: [
        {
          id: 'ryugatake-height',
          x: 0.25,
          y: 0.72,
          title: '標高1485メートル',
          body: '竜ヶ岳は標高1,485メートルの山で、本栖湖の南側に位置する。',
          source: 'https://fujisan.ne.jp/sightseeing/1537/',
          mapQuery: '竜ヶ岳 富士河口湖町',
        },
        {
          id: 'ryugatake-diamond',
          x: 0.5,
          y: 0.3,
          title: '冬至前後に見られる',
          body: '冬至前後には、山頂付近からダイヤモンド富士が見られることで知られる。',
          source: 'https://fujisan.ne.jp/sightseeing/1537/',
          mapQuery: '本栖湖キャンプ場',
        },
        {
          id: 'ryugatake-summit',
          x: 0.78,
          y: 0.75,
          title: '山頂は草原',
          body: '山頂からは本栖湖越しに富士山を望むことができ、山頂は草原状で、季節によっては霜や積雪が見られる。主な登山口は本栖湖キャンプ場の周辺で、標準のコースタイムは片道約2時間。',
          source: 'https://fujisan.ne.jp/sightseeing/1537/',
          mapQuery: '本栖湖',
        },
      ],
    },
    {
      id: 'fugaku-fuketsu',
      name: '富岳風穴',
      area: '富士河口湖町',
      image: '/assets/scenery/yamanashi/scenes/09_fugaku.webp',
      hotspots: [
        {
          id: 'fugaku-fuketsu-designation',
          x: 0.28,
          y: 0.3,
          title: '昭和4年に天然記念物',
          body: '国の天然記念物で、昭和4年12月17日の指定。',
          source:
            'https://www.pref.yamanashi.jp/bunka/bunkazaihogo/bunkazai_data/yamanashinobunkazai_nb0014.html',
          mapQuery: '富岳風穴',
        },
        {
          id: 'fugaku-fuketsu-length',
          x: 0.55,
          y: 0.65,
          title: '総延長258.7メートル',
          body: '風穴の総延長は258.7メートルで、富士北麓の溶岩洞穴の中では中規模。とくに延長80メートル以上という大支洞をもつことも一つの特徴である。',
          source:
            'https://www.pref.yamanashi.jp/bunka/bunkazaihogo/bunkazai_data/yamanashinobunkazai_nb0014.html',
          mapQuery: '富士河口湖町 青木ヶ原',
        },
        {
          id: 'fugaku-fuketsu-rope',
          x: 0.8,
          y: 0.4,
          title: '奥に縄状溶岩',
          body: '本洞の幅は4〜10メートル、高さは平均2〜3メートルだが、ところによっては0.9メートルあるいは6メートルというところもある。本洞の最も奥部には見事な縄状溶岩が形成されている。',
          source:
            'https://www.pref.yamanashi.jp/bunka/bunkazaihogo/bunkazai_data/yamanashinobunkazai_nb0014.html',
          mapQuery: '富岳風穴 溶岩洞穴',
        },
      ],
    },
    {
      id: 'sengataki',
      name: '仙娥滝',
      area: '甲府市',
      image: '/assets/scenery/yamanashi/scenes/10_sengataki.webp',
      hotspots: [
        {
          id: 'sengataki-height',
          x: 0.5,
          y: 0.3,
          title: '落差約30メートル',
          body: '落差は約30メートルで、日本の滝百選に選定されている滝。',
          source: 'https://kofu-tourism.com/spot/14',
          mapQuery: '仙娥滝',
        },
        {
          id: 'sengataki-fault',
          x: 0.25,
          y: 0.65,
          title: '断層でできた滝',
          body: '昇仙峡のシンボルである覚円峰の麓にあり、この滝は地殻変動による断層によって生じた。花崗岩の岩肌を削りながら落下している。',
          source: 'https://kofu-tourism.com/spot/14',
          mapQuery: '甲府市 仙娥滝',
        },
        {
          id: 'sengataki-location',
          x: 0.78,
          y: 0.7,
          title: '昇仙峡のいちばん奥',
          body: '昇仙峡の最奥部、覚円峰の麓に位置し、駐車場から徒歩約5分。新緑から紅葉、雪景色と四季にその美しさを装う。',
          source: 'https://kofu-tourism.com/spot/14',
          mapQuery: '昇仙峡 仙娥滝',
        },
      ],
    },
  ],
};
